import { z } from 'zod'

/** Restate journals and transports this key unchanged. Its suffix versions the payload. */
export const DOSS_ERROR_METADATA_KEY = 'doss.error.v1'
export const MAX_DOSS_ERROR_METADATA_BYTES = 64 * 1024

export const propagatedFailureSchema = z.object({
  message: z.string(),
  template: z.string().optional(),
  workflowV2Site: z.string().optional(),
  isCancelled: z.boolean().optional(),
  failedOperation: z
    .object({
      eventType: z.string(),
      entityType: z.string(),
      entityId: z.string().optional(),
      eventId: z.string().optional(),
    })
    .optional(),
})

/** The failure System Events and Radar share; params and retry policy are transport-only. */
export type PropagatedFailure = z.infer<typeof propagatedFailureSchema>

const dossErrorFailureSchema = propagatedFailureSchema.extend({
  params: z
    .array(z.union([z.string(), z.number(), z.boolean(), z.null()]))
    .readonly()
    .optional(),
  userFault: z.boolean().optional(),
  terminal: z.boolean().optional(),
  transportStatus: z.number().int().optional(),
})

export type DossErrorFailure = z.infer<typeof dossErrorFailureSchema>

export type DossErrorFailureEncodingResult =
  | { status: 'ok' | 'truncated'; value: string }
  | { status: 'invalid' | 'too_large' }

const textEncoder = new TextEncoder()

function byteLength(value: string): number {
  return textEncoder.encode(value).byteLength
}

/** Count escaped JSON bytes without splitting Unicode code points. */
function truncateMessage(message: string, maxBytes: number): string {
  const characters: string[] = []
  let bytes = 0
  for (const character of message) {
    bytes += byteLength(JSON.stringify(character)) - 2 // Exclude the surrounding quotes.
    if (bytes > maxBytes) {
      break
    }
    characters.push(character)
  }
  return characters.join('')
}

/** Keep identity and policy intact; callers can count truncation and rejected payloads by status. */
export function encodeDossErrorFailure(failure: DossErrorFailure): DossErrorFailureEncodingResult {
  const parsed = dossErrorFailureSchema.safeParse(failure)
  // Params are optional diagnostics. Invalid values must not discard the identity or policy.
  const validated = parsed.success
    ? parsed
    : dossErrorFailureSchema.safeParse({ ...failure, params: undefined })
  if (!validated.success) {
    return { status: 'invalid' }
  }
  const encoded = JSON.stringify(validated.data)
  if (byteLength(encoded) <= MAX_DOSS_ERROR_METADATA_BYTES) {
    return { status: parsed.success ? 'ok' : 'truncated', value: encoded }
  }

  const withoutParams = { ...validated.data, params: undefined }
  const encodedWithoutParams = JSON.stringify(withoutParams)
  if (byteLength(encodedWithoutParams) <= MAX_DOSS_ERROR_METADATA_BYTES) {
    return { status: 'truncated', value: encodedWithoutParams }
  }

  const essentialFields = { ...withoutParams, message: '' }
  const messageBudget = MAX_DOSS_ERROR_METADATA_BYTES - byteLength(JSON.stringify(essentialFields))
  if (messageBudget < 0) {
    return { status: 'too_large' }
  }
  return {
    status: 'truncated',
    value: JSON.stringify({
      ...essentialFields,
      message: truncateMessage(validated.data.message, messageBudget),
    }),
  }
}

/** Old journal entries have no payload; malformed metadata must not hide the original error. */
export function decodeDossErrorFailure(value: unknown): DossErrorFailure | undefined {
  if (typeof value !== 'string' || byteLength(value) > MAX_DOSS_ERROR_METADATA_BYTES) {
    return undefined
  }
  try {
    const parsed = dossErrorFailureSchema.safeParse(JSON.parse(value))
    return parsed.success ? parsed.data : undefined
  } catch {
    return undefined
  }
}
